import { TableCell } from "@mui/material";
import * as React from "react";
import Select, { components } from "react-select";
import { reactSelectSMStyle, styles } from "../../Utils/Common";

export interface CorrectiveActionOption {
    value: string | number;
    label: string;
    isCustom?: boolean;
}

interface CorrectiveActionCellProps {
    value: string | number | null | undefined;
    dropdownOptions: CorrectiveActionOption[];
    isRequired?: boolean;
    deleteEnabled?: boolean;
    showAddNewInput: boolean;
    newActionText: string;
    newActionError: string;
    newActionInputRef: React.RefObject<HTMLInputElement>;
    onChange: (value: string | number) => void;
    onAddNewClick: () => void;
    onNewActionTextChange: (text: string) => void;
    onSaveNewAction: () => void;
    onCancelNewAction: () => void;
    onDeleteOption: (option: CorrectiveActionOption) => void;
    t: (key: string) => string;
}

function CorrectiveActionCell({
    value,
    dropdownOptions,
    isRequired,
    deleteEnabled = false,
    showAddNewInput,
    newActionText,
    newActionError,
    newActionInputRef,
    onChange,
    onAddNewClick,
    onNewActionTextChange,
    onSaveNewAction,
    onCancelNewAction,
    onDeleteOption,
    t,
}: CorrectiveActionCellProps) {
    const selectedOption = dropdownOptions.find(
        (option) => String(option.value) === String(value ?? "")
    );

    const OptionComponent = React.useCallback(
        (props: any) => {
            const option = props.data as CorrectiveActionOption;
            const showDelete = deleteEnabled;

            return (
                <components.Option {...props}>
                    <div className="d-flex align-items-center justify-content-between gap-2 w-100 corrective-action-option">
                        <span className="text-truncate flex-grow-1 min-w-0">
                            {option.label}
                        </span>
                        {showDelete && (
                            <button
                                type="button"
                                className="btn btn-icon btn-sm corrective-action-delete-btn flex-shrink-0"
                                title={t("Delete")}
                                aria-label={t("Delete")}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onDeleteOption(option);
                                }}
                            >
                                <i className="bi bi-trash3 text-danger" />
                            </button>
                        )}
                    </div>
                </components.Option>
            );
        },
        [deleteEnabled, onDeleteOption, t]
    );

    return (
        <TableCell style={{ padding: "12px 16px", verticalAlign: "top" }}>
            <div className="corrective-action-cell d-flex flex-column gap-2 w-100">
                <div className="d-flex align-items-center gap-2 w-100">
                    <div
                        className={`corrective-action-select-wrap flex-grow-1${
                            isRequired ? " is-required" : ""
                        }`}
                        style={{ minWidth: 0 }}
                    >
                        <Select
                            menuPortalTarget={document.body}
                            theme={(theme) => styles(theme)}
                            name="CorrectiveActionReasonId"
                            value={selectedOption ?? null}
                            options={dropdownOptions}
                            components={{ Option: OptionComponent }}
                            styles={{
                                ...reactSelectSMStyle,
                                control: (base: any, state: any) => ({
                                    ...reactSelectSMStyle.control(base, state),
                                    width: "100%",
                                    minHeight: "32px",
                                }),
                                menu: (base: any) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                                menuPortal: (base: any) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                                option: (base: any, state: any) => ({
                                    ...base,
                                    padding: "6px 10px",
                                    cursor: state.isDisabled ? "not-allowed" : "pointer",
                                }),
                            }}
                            placeholder={t("Select")}
                            onChange={(selected: any) => {
                                if (selected?.value != null) {
                                    onChange(selected.value);
                                }
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-light-primary text-nowrap corrective-action-add-btn"
                        onClick={onAddNewClick}
                    >
                        <i className="bi bi-plus-lg me-1" />
                        {t("Add New")}
                    </button>
                </div>

                {showAddNewInput && (
                    <div className="corrective-action-add-panel d-flex flex-column gap-1">
                        <div className="d-flex align-items-center gap-2">
                            <input
                                ref={newActionInputRef}
                                type="text"
                                className={`form-control bg-white h-32px rounded-2 corrective-action-add-input${
                                    newActionError ? " is-invalid" : ""
                                }`}
                                placeholder={t("Enter corrective action")}
                                value={newActionText}
                                onChange={(event) =>
                                    onNewActionTextChange(event.target.value)
                                }
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        onSaveNewAction();
                                    }
                                    if (event.key === "Escape") {
                                        event.preventDefault();
                                        onCancelNewAction();
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-success text-nowrap corrective-action-text-btn"
                                onClick={onSaveNewAction}
                            >
                                {t("Save")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary text-nowrap corrective-action-text-btn"
                                onClick={onCancelNewAction}
                            >
                                {t("Cancel")}
                            </button>
                        </div>
                        {newActionError && (
                            <div className="invalid-feedback d-block">
                                {newActionError}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </TableCell>
    );
}

export default CorrectiveActionCell;
